import { useEffect, useMemo, useState } from "react";
import { Box, CssBaseline, Typography, Paper, Grid, Link, Divider, CircularProgress, IconButton } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AppTheme from "../../../theme/AppTheme";
import { dataGridCustomizations } from "../../../theme/customizations";
import { getCampaignAnalyticsOverview } from "../../../service/BuyerConfiguration/CampaignManagement/campaignService";
import { notifyError } from "../../../components/ToastContainer/Toast/Toast";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";

type OverviewResponse = {
  period?: { from?: string; to?: string };
  campaigns?: { total?: number; active?: number };
  coupons?: { total?: number; active?: number };
  funnel?: {
    reserved?: number;
    redeemed?: number;
    rolled_back?: number;
    failed?: number;
    redemption_rate?: number;
  };
  financials?: {
    total_discount_amount?: number;
    avg_discount_amount?: number;
    delivery_waived_count?: number;
    orders_with_coupon?: number;
  };
};

const formatDateOnly = (dateString?: string) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const isValidDateString = (value?: string | null) => {
  if (value === undefined || value === null) return false;
  const s = String(value).trim();
  if (!s.length) return false;
  const t = new Date(s).getTime();
  return !Number.isNaN(t);
};

const StatCard = ({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      p: 2.5,
      boxShadow: "none",
      background:
        "linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(0,0,0,0.00) 60%)",
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {subtitle ? (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    ) : null}
  </Paper>
);

const CampaignDashboard = (props: { disableCustomTheme?: boolean }) => {
  const xThemeComponents = useMemo(() => ({ ...dataGridCustomizations }), []);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  const periodDisplay = useMemo(() => {
    const apiFrom = overview?.period?.from;
    const apiTo = overview?.period?.to;
    if (isValidDateString(apiFrom) && isValidDateString(apiTo)) {
      return { mode: "range" as const, from: apiFrom as string, to: apiTo as string };
    }
    if (fromDate && toDate) {
      return {
        mode: "range" as const,
        from: fromDate.startOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
        to: toDate.endOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      };
    }
    return { mode: "allTime" as const };
  }, [overview?.period?.from, overview?.period?.to, fromDate, toDate]);

  useEffect(() => {
    const fetchOverview = async (params?: { from?: string; to?: string }) => {
      try {
        setLoading(true);
        const res = await getCampaignAnalyticsOverview(params);
        const data: OverviewResponse =
          res?.data?.data || res?.data || res?.data?.overview || res?.overview || null;
        setOverview(data);
      } catch (e: any) {
        notifyError(e?.response?.data?.message || "Failed to load campaign analytics overview");
        setOverview(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 2,
            flexDirection: { xs: "column", sm: "row" },
            rowGap: 1.5,
          }}
        >
          <Box>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 800 }}>
              Campaign Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track campaign performance and coupon activity at a glance.
            </Typography>
          </Box>
          <Box sx={{ ml: "auto", alignSelf: { xs: "flex-start", sm: "auto" } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
                <Box sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    maxDate={dayjs()}
                    onChange={(v) => {
                      setFromDate(v);
                      if (!v) setToDate(null);
                    }}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: { xs: "100%", sm: 160 },
                          "& .MuiInputBase-root": { height: 40 },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    minDate={dayjs()}
                    onChange={async (v) => {
                      setToDate(v);
                      if (!v) return;
                      try {
                        setLoading(true);
                        const fromIso = (fromDate ?? v).startOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ');
                        const toIso = v.endOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ');
                        const res = await getCampaignAnalyticsOverview({ from: fromIso, to: toIso });
                        const data: OverviewResponse =
                          res?.data?.data || res?.data || res?.data?.overview || res?.overview || null;
                        setOverview(data);
                      } catch (e: any) {
                        notifyError(e?.response?.data?.message || "Failed to load campaign analytics overview");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: { xs: "100%", sm: 160 },
                          "& .MuiInputBase-root": { height: 40 },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ mb: { xs: 1, sm: 0 } }}>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Reset date filters"
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                    onClick={async () => {
                      setFromDate(null);
                      setToDate(null);
                      try {
                        setLoading(true);
                        const res = await getCampaignAnalyticsOverview();
                        const data: OverviewResponse =
                          res?.data?.data || res?.data || res?.data?.overview || res?.overview || null;
                        setOverview(data);
                      } catch (e: any) {
                        notifyError(e?.response?.data?.message || "Failed to load campaign analytics overview");
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </LocalizationProvider>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2.5,
            boxShadow: "none",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "flex-start" }}>
            <Box sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}>
              {periodDisplay.mode === "allTime" ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Overall Campaign Details (All Time)
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Period
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.25 }}>
                    {formatDateOnly(periodDisplay.from)} — {formatDateOnly(periodDisplay.to)}
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ ml: "auto", display: "flex", alignItems: "flex-start" }}>
              <Link
                component={RouterLink}
                to="/campaign-management/list"
                underline="hover"
                sx={{ fontWeight: 700, mt: 0.1 }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/campaign-management/list");
                }}
              >
                View Campaign Management
              </Link>
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", mt: { xs: 1, sm: 0 } }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading overview…
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Paper>

        <Grid container sx={{ mx: { xs: 0, md: -1 } }}>
          <Grid item xs={12} md={3}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard
                title="Total Campaigns"
                value={overview?.campaigns?.total ?? "-"}
                subtitle={`Active: ${overview?.campaigns?.active ?? "-"}`}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard
                title="Total Coupons"
                value={overview?.coupons?.total ?? "-"}
                subtitle={`Active: ${overview?.coupons?.active ?? "-"}`}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard
                title="Redeemed"
                value={overview?.funnel?.redeemed ?? "-"}
                subtitle={`Reserved: ${overview?.funnel?.reserved ?? "-"} · Failed: ${overview?.funnel?.failed ?? "-"}`}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
              <StatCard
                title="Redemption Rate"
                value={
                  overview?.funnel?.redemption_rate !== undefined &&
                  overview?.funnel?.redemption_rate !== null
                    ? `${overview.funnel.redemption_rate}%`
                    : "-"
                }
                subtitle={`Rolled Back: ${overview?.funnel?.rolled_back ?? "-"}`}
              />
            </Box>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: "transparent",
            borderRadius: 2,
            p: 2.5,
            boxShadow: "none",
            mt: 2,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 800, mb: 1 }}>
            Financials
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container sx={{ mx: { xs: 0, md: -1 } }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard
                  title="Total Discount Amount"
                  value={overview?.financials?.total_discount_amount ?? "-"}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard
                  title="Avg Discount Amount"
                  value={overview?.financials?.avg_discount_amount ?? "-"}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard
                  title="Delivery Waived"
                  value={overview?.financials?.delivery_waived_count ?? "-"}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ mx: { xs: 0, md: 1 }, mb: { xs: 2, md: 2 } }}>
                <StatCard
                  title="Orders With Coupon"
                  value={overview?.financials?.orders_with_coupon ?? "-"}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </AppTheme>
  );
};

export default CampaignDashboard;

